<template>
  <div v-if="displayClaims.length" :data-test-id="dataTestId" :class="outerClass" :style="inlineStyle">
    <PanelTitle :title="props.fields?.title!" :subTitle="props.fields?.subtitle!" />
    <div class="grid gap-3 mt-6 racq-max-w-xl5">
      <Notification
        v-for="(claim, index) in displayClaims"
        :key="index"
        :type="InformationBoxTypes.INFO"
        :title="claim?.title"
      >
        <template #buttons>
          <div class="text-m md:mt-4 mt-2">
            <RichText class="text-m-bold" :field="{ value: toProperCase(claim?.coverageTitle) }" />
            <RichText class="text-m-bold" :field="{ value: claim?.claimRef }" />
            <RichText v-if="claim?.claimDetails" :field="{ value: claim?.claimDetails }" />
            <RichText :field="{ value: claim?.expiryDetails }" />
            <button class="mt-4 btn btn-sm btn-primary" @click="() => resumeClaim(claim?.saveUrl)">
              {{ props.fields?.resumeBtn?.value }}
            </button>
          </div>
        </template>
      </Notification>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useAccountStore, usePageStore } from '@/store';
import { useSelfServiceInsuranceClaims } from '@/store/self-service/insurance-claims.state';
import { computed, ref, watch, onMounted } from 'vue';
import PanelTitle from '../PanelTitle/PanelTitle.vue';
import { ComponentRendering, Field, RichText } from '@sitecore-jss/sitecore-jss-vue';
import Notification from '../Notification/Notification.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { getInsuranceTypeDisplayName, IIncompleteClaim } from '@/interfaces';
import { useI18n } from 'vue-i18n';
import { getCoverageTitle } from '@/utils/self-service-rules/InsurancePolicyStatusRules';
import { dateToDateString } from '@/utils/data-transforms';
import { useRoute } from 'vue-router';
import { toProperCase } from '@/utils/text-transforms';

const props = defineProps<{
  fields?: Record<string, Field<string>>;
  rendering?: ComponentRendering;
}>();
const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const route = useRoute();

const outerClass = props.rendering?.params?.Styles;
const inlineStyle = props.rendering?.params?.inlineStyle;
const dataTestId = usePageStore().dataTestId(props.rendering);
const accountStore = useAccountStore();
const insuranceClaimsStore = useSelfServiceInsuranceClaims();
const incompleteClaims = ref<IIncompleteClaim[]>([]);

const emit = defineEmits(['hasIncompleteClaims']);

onMounted(() => {
  fetchClaims();
});

watch(
  () => accountStore.identityDetails?.portfolio?.insurancePolicies,
  () => {
    fetchClaims();
  }
);

const fetchClaims = () => {
  const policyNumbers = accountStore.identityDetails?.portfolio?.insurancePolicies
    ?.map((policy) => policy.policyNumber) // Map policy number
    .filter((policyNumber) => !route?.query?.policyNumber || route?.query?.policyNumber == policyNumber); // Filter for product details view
  policyNumbers?.length &&
    insuranceClaimsStore.getIncompleteClaims(policyNumbers).then((res) => (incompleteClaims.value = res));
};

const displayClaims = computed(() => {
  return incompleteClaims.value
    .map((claim) => {
      // get policy details
      const policy = accountStore.identityDetails?.portfolio?.insurancePolicies?.find(
        (policy) => policy.policyNumber == claim.policyNumber
      );
      if (!policy) return;
      // map display lines

      //try and find coverage for motor
      let coverage = policy?.coverages.find(
        (x: any) => x.vehicle?.registrationNumber?.toLowerCase() == claim.claimItem?.toLowerCase()
      );

      //try and find coverage for home
      if (!coverage) {
        coverage = policy?.coverages.find(
          (x: any) =>
            x.riskAddress?.formattedLong?.toLowerCase()?.replaceAll(',', '') ==
            claim.claimItem?.toLowerCase()?.replaceAll(',', '')
        );
      }

      //try and find coverage for pet
      if (!coverage) {
        coverage = policy?.coverages.find(
          (x: any) =>
            x.petName?.toLowerCase()?.replaceAll(',', '') == claim.claimItem?.toLowerCase()?.replaceAll(',', '')
        );
      }

      const coverType = getInsuranceTypeDisplayName(dictionary, coverage?.coverageCode ?? '', false);

      let claimDetails: string | undefined;
      // Pet claims could have no incident date
      if (claim.incidentDate) {
        //if a dash is detected it indicates that claims details should be formatted like this with a dash included after the loss type: 'Fire - occurred on 15/08/2025'.
        const appendDash =
          props.fields?.claimDetails?.value?.trim()?.startsWith('-') && claim.lossCategory.length > 0 ? '-' : '';

        claimDetails = props.fields?.claimDetails?.value
          ?.replace('{{lossCategory}}', `${claim.lossCategory} ${appendDash} `)
          .replace('{{incidentDate}}', dateToDateString(claim.incidentDate, 'dd/MM/yyyy'));

        if (appendDash.length > 0 && claimDetails) {
          const splitDetails = claimDetails.split('-');
          claimDetails = `${splitDetails[1]} - ${splitDetails[2]}`;
        } else {
          claimDetails = claimDetails?.replace('-', '').trim();
          if (claimDetails) {
            claimDetails = `${claimDetails.charAt(0).toUpperCase()}${claimDetails?.slice(1)}`;
          }
        }
      }

      return {
        title: props.fields?.brief?.value?.replace('{{coverType}}', coverType),
        coverageTitle: getCoverageTitle(policy, claim.claimItem),
        claimRef: props.fields?.claimRef?.value?.replace('{{claimNumber}}', claim.referenceNumber),
        claimDetails,
        expiryDetails: props.fields?.expiryDetails?.value
          ?.replace('{{savedAt}}', dateToDateString(claim.savedAt, 'dd/MM/yyyy'))
          .replace('{{daysExpiry}}', claim.daysExpiry?.toString()),
        saveUrl: claim.saveUrl,
      };
    })
    .filter((o) => o);
});

watch(
  () => displayClaims?.value?.length,
  () => {
    emit('hasIncompleteClaims', displayClaims?.value?.length > 0);
  }
);

const resumeClaim = (url: string | undefined) => {
  url && (window.location.href = url);
};
</script>
